import get from 'lodash/get';
import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import mint from '@tunein/mint';
import {
  WEB_TUNER_AUTOPLAY_ENABLED,
  WEB_TUNER_INSTREAM_LOGGING_ENABLED,
  WEB_TUNER_SHOUTCAST_EXTRA_TIMEOUT,
  WEB_TUNER_STREAM_RETRY_LIMIT,
  WEB_TUNER_STREAM_TIMEOUT,
} from 'src/common/constants/experiments/config';
import { selectExperiment } from '../../selectors/config';
import { primePlayerForAutoPlay } from '../../actions/player';
import { logClientError, logFeatureActivity } from '../../actions/logging';
import * as TunerActions from '../../actions/tuner';
import isServer from '../../utils/isServer';
import { selectAutoPlayReady } from '../../selectors/player';
import { selectHlsJsAssetUrl } from '../../selectors/app';
import { isProfile } from '../../utils/guideItemTypes';
import feature from '../../constants/analytics/categoryActionLabel/feature';
import shouldShowPlayer from '../../utils/playerStatus/shouldShowPlayer';
import { HOWLER_ENABLED } from '../../constants/experiments/player';
import setupTuner from './setupTuner';
import Player from './Player';

class WithPlayer extends Component {
  static propTypes = {
    timeout: PropTypes.number.isRequired,
    retryLimit: PropTypes.number.isRequired,
    shoutcastExtraTimeout: PropTypes.number.isRequired,
    isInstreamLoggingEnabled: PropTypes.bool.isRequired,
    isAutoPlayEnabled: PropTypes.bool.isRequired,
    isHowlerEnabled: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    hlsjsAssetUrl: PropTypes.string.isRequired,
    breakpoint: PropTypes.number.isRequired,
    routeProps: PropTypes.object.isRequired,
    showPlayer: PropTypes.bool.isRequired,
    apiClient: PropTypes.object, // Not required since we won't get this on the server.
    history: PropTypes.object,
    autoPlayReady: PropTypes.bool,
  };

  eventMap = {};

  constructor(props) {
    super(props);

    // Setup the web-tuner and event listeners here, but only on the client.
    if (!isServer()) {
      // We need to load jplayer before web-tuner is set up, as it's required to be injected into
      // the jquery namespace before stream player is setup, so require it here before the tuner
      // setup.
      require('jplayer'); // eslint-disable-line global-require

      const playerElement = document.createElement('div');
      playerElement.id = 'player';
      document.body.appendChild(playerElement);
      const {
        timeout,
        retryLimit,
        shoutcastExtraTimeout,
        isInstreamLoggingEnabled,
        isHowlerEnabled,
        hlsjsAssetUrl,
        apiClient,
        actions,
      } = props;

      const tunerConfigs = {
        timeout,
        retryLimit,
        shoutcastExtraTimeout,
        isInstreamLoggingEnabled,
        isHowlerEnabled,

        // hasNativeHlsSupport: Instruction passed to the web-tuner that is used
        // whether to use JPlayer and the native HTML Media Element browser implementation
        // to play HLS stream. If there is no native support, then the web-tuner will
        // use an HLS media player library to play HLS format streams.
        hasNativeHlsSupport: false,

        // resources: The player library asset urls that the web-tuner requires to load a
        // given media player library.
        resources: {
          hlsjs: hlsjsAssetUrl,
        },
      };

      this.tuner = setupTuner(apiClient, playerElement, tunerConfigs);
      const { events } = this.tuner;
      this.eventMap = {
        [events.meta]: actions.meta,
        [events.connecting]: actions.connecting,
        [events.playing]: actions.playing,
        [events.nowPlayingChanged]: actions.nowPlayingChanged,
        [events.mediaEnded]: actions.mediaEnded,
        [events.streamFailed]: actions.streamFailed,
        [events.stationFailed]: actions.stationFailed,
        [events.waiting]: actions.waiting,
        [events.qualifiedTune]: actions.qualifiedTune,
        [events.htmlStreamLoaded]: actions.htmlStreamLoaded,
        [events.evaluateStreamStart]: actions.evaluateStreamStart,
        [events.positionChanged]: actions.positionChanged,
        [events.nextTune]: this.onNextTune,
        [events.instreamAd]: this.onInstreamAd,
        [events.error]: actions.logClientError,
        [events.playerSelected]: actions.playerSelected,
        [events.attemptingPlayback]: actions.clearPlayerNameList,
      };
    }
  }

  componentDidMount() {
    const { isAutoPlayEnabled, actions, routeProps } = this.props;
    const guideId = get(routeProps, 'guideContext.guideId');
    const isProfileRoute = isProfile(guideId);

    if (isAutoPlayEnabled && !isProfileRoute) {
      actions.primePlayerForAutoPlay();
    }

    this.registerListeners();
  }

  componentDidUpdate(prevProps) {
    const { actions, autoPlayReady } = this.props;
    if (!prevProps.autoPlayReady && autoPlayReady) {
      const { actions: featureActions, labels: featureLabels } = feature;
      actions.logFeatureActivity(
        featureActions.autoPlay,
        featureLabels.showMiniPlayer,
      );
    }
  }

  componentWillUnmount() {
    if (!isServer()) {
      this.removeListeners();
    }
  }

  onNextTune = (nextTune) => {
    this.props.actions.nextTune(nextTune, this.props.history);
  };

  // the tuner selects the player that can handle instream ads based on the stream,
  // when it receives the ad data, it's passed here so that we can pass it to mint
  onInstreamAd(metadata) {
    mint.handleInstreamAds(metadata);
  }

  registerListeners() {
    const keys = Object.keys(this.eventMap);
    keys.forEach((key) => {
      const value = this.eventMap[key];
      this.tuner.on(key, value);
    });
  }

  removeListeners() {
    const keys = Object.keys(this.eventMap);
    keys.forEach((key) => {
      const value = this.eventMap[key];
      this.tuner.off(key, value);
    });
  }

  render() {
    const { breakpoint, showPlayer } = this.props;
    return showPlayer ? <Player breakpoint={breakpoint} /> : null;
  }
}

export const mapStateToProps = (state) => ({
  hlsjsAssetUrl: selectHlsJsAssetUrl(state),
  autoPlayReady: selectAutoPlayReady(state),
  timeout: selectExperiment(state, WEB_TUNER_STREAM_TIMEOUT),
  retryLimit: selectExperiment(state, WEB_TUNER_STREAM_RETRY_LIMIT),
  shoutcastExtraTimeout: selectExperiment(
    state,
    WEB_TUNER_SHOUTCAST_EXTRA_TIMEOUT,
  ),
  isInstreamLoggingEnabled: selectExperiment(
    state,
    WEB_TUNER_INSTREAM_LOGGING_ENABLED,
  ),
  isAutoPlayEnabled: selectExperiment(state, WEB_TUNER_AUTOPLAY_ENABLED),
  isHowlerEnabled: selectExperiment(state, HOWLER_ENABLED),
  showPlayer: shouldShowPlayer(state),
});

export const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      logFeatureActivity,
      primePlayerForAutoPlay,
      logClientError,
      playing: TunerActions.playing,
      connecting: TunerActions.connecting,
      nowPlayingChanged: TunerActions.nowPlayingChanged,
      mediaEnded: TunerActions.mediaEnded,
      streamFailed: TunerActions.streamFailed,
      stationFailed: TunerActions.stationFailed,
      waiting: TunerActions.waiting,
      qualifiedTune: TunerActions.qualifiedTune,
      htmlStreamLoaded: TunerActions.handleHtmlStream,
      nextTune: TunerActions.nextTune,
      evaluateStreamStart: TunerActions.handleStreamEvaluation,
      positionChanged: TunerActions.positionChanged,
      meta: TunerActions.handleMeta,
      playerSelected: TunerActions.playerSelected,
      clearPlayerNameList: TunerActions.clearPlayerNameList,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(WithPlayer);
